import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActionArea,
  Chip,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  Stack,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  ShowChart as LineChartIcon,
  TableChart as TableChartIcon,
  Save as SaveIcon,
  Schedule as ScheduleIcon,
  Download as DownloadIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { format, subDays } from 'date-fns';
import { alpha, useTheme } from '@mui/material/styles';
import { useNotify } from '@/stores/notificationStore';
import { useServices } from '@/hooks/useServices';
import { useMechanics } from '@/hooks/useStaffUsers';
import { chartColors, neutralGrey } from '@/styles/theme';
import { chartTooltipProps } from '@/lib/chartTheme';
import { dateFnsLocale, formatNumber } from '@/utils/locale';

// Report configuration types
type MetricType =
  | 'revenue'
  | 'workOrders'
  | 'laborHours'
  | 'partsSales'
  | 'customerCount'
  | 'avgTicketValue'
  | 'mechanicProductivity'
  | 'customerSatisfaction'
  | 'agentPerformance';

type ChartType = 'line' | 'area' | 'bar' | 'pie' | 'table';
type TimeGrouping = 'day' | 'week' | 'month' | 'quarter' | 'year';
type ComparisonType = 'none' | 'previous-period' | 'year-over-year';

interface ReportConfig {
  name: string;
  metrics: MetricType[];
  chartType: ChartType;
  timeGrouping: TimeGrouping;
  dateFrom: Date;
  dateTo: Date;
  comparison: ComparisonType;
  filters: {
    mechanicIds?: string[];
    serviceTypes?: string[];
    customerStatuses?: string[];
  };
  schedule?: {
    enabled: boolean;
    frequency: 'daily' | 'weekly' | 'monthly';
    recipients: string[];
  };
}

const COLORS = [...chartColors];

const METRIC_ICONS: Record<MetricType, { icon: React.ReactNode; color: string }> = {
  revenue: { icon: <TrendingUpIcon />, color: chartColors[0] },
  workOrders: { icon: <BarChartIcon />, color: chartColors[1] },
  laborHours: { icon: <LineChartIcon />, color: chartColors[2] },
  partsSales: { icon: <PieChartIcon />, color: chartColors[3] },
  customerCount: { icon: <TableChartIcon />, color: chartColors[5] },
  avgTicketValue: { icon: <TrendingUpIcon />, color: chartColors[4] },
  mechanicProductivity: { icon: <BarChartIcon />, color: neutralGrey },
  customerSatisfaction: { icon: <LineChartIcon />, color: chartColors[1] },
  agentPerformance: { icon: <TrendingUpIcon />, color: chartColors[0] },
};

export default function ReportBuilderPage() {
  const { t } = useTranslation('reports');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const theme = useTheme();
  const { data: services = [] } = useServices({ isActive: true });

  const METRIC_DEFINITIONS: Record<
    MetricType,
    { label: string; description: string; icon: React.ReactNode; color: string }
  > = {
    revenue: { label: t('builder.metrics.revenue'), description: t('builder.metrics.revenueDesc'), ...METRIC_ICONS.revenue },
    workOrders: { label: t('builder.metrics.workOrders'), description: t('builder.metrics.workOrdersDesc'), ...METRIC_ICONS.workOrders },
    laborHours: { label: t('builder.metrics.laborHours'), description: t('builder.metrics.laborHoursDesc'), ...METRIC_ICONS.laborHours },
    partsSales: { label: t('builder.metrics.partsSales'), description: t('builder.metrics.partsSalesDesc'), ...METRIC_ICONS.partsSales },
    customerCount: { label: t('builder.metrics.customerCount'), description: t('builder.metrics.customerCountDesc'), ...METRIC_ICONS.customerCount },
    avgTicketValue: { label: t('builder.metrics.avgTicketValue'), description: t('builder.metrics.avgTicketValueDesc'), ...METRIC_ICONS.avgTicketValue },
    mechanicProductivity: { label: t('builder.metrics.mechanicProductivity'), description: t('builder.metrics.mechanicProductivityDesc'), ...METRIC_ICONS.mechanicProductivity },
    customerSatisfaction: { label: t('builder.metrics.customerSatisfaction'), description: t('builder.metrics.customerSatisfactionDesc'), ...METRIC_ICONS.customerSatisfaction },
    agentPerformance: { label: t('builder.metrics.agentPerformance'), description: t('builder.metrics.agentPerformanceDesc'), ...METRIC_ICONS.agentPerformance },
  };

  const mechanics = useMechanics();

  const [activeStep, setActiveStep] = useState(0);
  const [config, setConfig] = useState<ReportConfig>({
    name: '',
    metrics: [],
    chartType: 'line',
    timeGrouping: 'day',
    dateFrom: subDays(new Date(), 30),
    dateTo: new Date(),
    comparison: 'none',
    filters: {},
  });

  const steps = [t('builder.steps.selectMetrics'), t('builder.steps.timeAndFilters'), t('builder.steps.selectVisualization'), t('builder.steps.previewAndSave')];

  // Generate sample data based on config
  const chartData = useMemo(() => {
    const days = Math.ceil((config.dateTo.getTime() - config.dateFrom.getTime()) / (1000 * 60 * 60 * 24));
    const data = [];

    for (let i = 0; i < Math.min(days, 90); i++) {
      const date = subDays(config.dateTo, days - i - 1);
      const dataPoint: { date: string; [metric: string]: string | number } = {
        date: format(date, 'yyyy-MM-dd'),
      };

      config.metrics.forEach((metric) => {
        switch (metric) {
          case 'revenue':
            dataPoint.revenue = Math.floor(Math.random() * 50000) + 100000;
            break;
          case 'workOrders':
            dataPoint.workOrders = Math.floor(Math.random() * 15) + 5;
            break;
          case 'laborHours':
            dataPoint.laborHours = Math.floor(Math.random() * 40) + 20;
            break;
          case 'partsSales':
            dataPoint.partsSales = Math.floor(Math.random() * 30000) + 50000;
            break;
          case 'customerSatisfaction':
            dataPoint.satisfaction = (Math.random() * 1 + 4).toFixed(1);
            break;
        }
      });

      data.push(dataPoint);
    }

    return data;
  }, [config.metrics, config.dateFrom, config.dateTo]);

  const handleNext = () => {
    if (activeStep === 0 && config.metrics.length === 0) {
      notify.warning(t('builder.selectAtLeastOneMetric'));
      return;
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setActiveStep((prev) => prev - 1);
  };

  const handleMetricToggle = (metric: MetricType) => {
    setConfig((prev) => ({
      ...prev,
      metrics: prev.metrics.includes(metric)
        ? prev.metrics.filter((m) => m !== metric)
        : [...prev.metrics, metric],
    }));
  };

  /**
   * Saving is not implemented — there is no saved-report storage in the API
   * (#2712). This used to answer with "Raportti tallennettu!", so the user
   * believed a report existed that did not.
   */
  const handleSaveReport = () => {
    if (!config.name) {
      notify.warning(t('builder.enterReportName'));
      return;
    }
    notify.warning(t('builder.saveUnavailable'), t('builder.saveUnavailableDesc'));
  };

  /**
   * The preview table is randomised sample data, so exporting it would hand
   * the user a file of invented figures. The real export lives on the
   * reports page, which works from actual data.
   */
  const handleExport = () => {
    notify.warning(t('builder.exportUnavailable'), t('builder.exportUnavailableDesc'));
  };

  const renderChart = () => {
    if (chartData.length === 0) return null;

    switch (config.chartType) {
      case 'line':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => format(new Date(date), 'd.M', { locale: dateFnsLocale() })}
              />
              <YAxis />
              <Tooltip
                {...chartTooltipProps(theme)}
                labelFormatter={(date) => format(new Date(date), 'd.M.yyyy', { locale: dateFnsLocale() })}
              />
              <Legend />
              {config.metrics.map((metric, index) => (
                <Line
                  key={metric}
                  type="monotone"
                  dataKey={metric}
                  stroke={COLORS[index % COLORS.length]}
                  strokeWidth={2}
                  name={METRIC_DEFINITIONS[metric].label}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        );

      case 'area':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <AreaChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => format(new Date(date), 'd.M', { locale: dateFnsLocale() })}
              />
              <YAxis />
              <Tooltip
                {...chartTooltipProps(theme)}
                labelFormatter={(date) => format(new Date(date), 'd.M.yyyy', { locale: dateFnsLocale() })}
              />
              <Legend />
              {config.metrics.map((metric, index) => (
                <Area
                  key={metric}
                  type="monotone"
                  dataKey={metric}
                  stackId="1"
                  stroke={COLORS[index % COLORS.length]}
                  fill={COLORS[index % COLORS.length]}
                  name={METRIC_DEFINITIONS[metric].label}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        );

      case 'bar':
        return (
          <ResponsiveContainer width="100%" height={400}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                tickFormatter={(date) => format(new Date(date), 'd.M', { locale: dateFnsLocale() })}
              />
              <YAxis />
              <Tooltip
                {...chartTooltipProps(theme)}
                labelFormatter={(date) => format(new Date(date), 'd.M.yyyy', { locale: dateFnsLocale() })}
              />
              <Legend />
              {config.metrics.map((metric, index) => (
                <Bar
                  key={metric}
                  dataKey={metric}
                  fill={COLORS[index % COLORS.length]}
                  name={METRIC_DEFINITIONS[metric].label}
                />
              ))}
            </BarChart>
          </ResponsiveContainer>
        );

      case 'pie': {
        // For pie chart, sum up the values
        const pieData = config.metrics.map((metric, index) => {
          const total = chartData.reduce((sum, item) => {
            const value = item[metric];
            return sum + (typeof value === 'number' ? value : 0);
          }, 0);
          return {
            name: METRIC_DEFINITIONS[metric].label,
            value: total,
            color: COLORS[index % COLORS.length],
          };
        });

        return (
          <ResponsiveContainer width="100%" height={400}>
            <PieChart>
              <Pie
                data={pieData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="50%"
                outerRadius={120}
                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              >
                {pieData.map((entry, index) => (
                  <Cell key={index} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip {...chartTooltipProps(theme)} />
            </PieChart>
          </ResponsiveContainer>
        );
      }

      case 'table':
        return (
          <Box sx={{ overflowX: 'auto', maxHeight: 400 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${theme.palette.divider}` }}>
                  <th style={{ padding: '12px 8px', textAlign: 'left' }}>{tc('date')}</th>
                  {config.metrics.map((metric) => (
                    <th key={metric} style={{ padding: '12px 8px', textAlign: 'right' }}>
                      {METRIC_DEFINITIONS[metric].label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {chartData.slice(-10).map((row, index) => (
                  <tr key={index} style={{ borderBottom: `1px solid ${theme.palette.grey[100]}` }}>
                    <td style={{ padding: '8px' }}>
                      {format(new Date(row.date), 'd.M.yyyy', { locale: dateFnsLocale() })}
                    </td>
                    {config.metrics.map((metric) => {
                      const value = row[metric];
                      return (
                        <td key={metric} style={{ padding: '8px', textAlign: 'right' }}>
                          {typeof value === 'number' ? formatNumber(value) : (value ?? '-')}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={600} gutterBottom>
            {t('builder.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('builder.description')}
          </Typography>
        </Box>
      </Box>

      {/*
        This page is a layout prototype, not a working report (#2712). Every
        figure below comes from Math.random() and there is no API call in the
        file, so the charts show different numbers on each render.

        It used to claim otherwise — "Raportti tallennettu!" from a handler
        that only raised a toast. Saying so plainly is the honest state until
        the builder is wired to /reports/*; a user who is told cannot be
        misled by the numbers.
      */}
      <Alert severity="warning" sx={{ mb: 3 }}>
        {t('builder.prototypeNotice')}
      </Alert>

      <Paper elevation={0} sx={{ border: 1, borderColor: 'divider', mb: 3 }}>
        <Stepper activeStep={activeStep} sx={{ p: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Paper>

      {/* Step 1: Select Metrics */}
      {activeStep === 0 && (
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('builder.steps.selectMetrics')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('builder.selectMetricsDesc')}
          </Typography>

          <Grid container spacing={2}>
            {Object.entries(METRIC_DEFINITIONS).map(([key, def]) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                <Card
                  variant="outlined"
                  sx={{
                    height: '100%',
                    border: 2,
                    borderColor: config.metrics.includes(key as MetricType)
                      ? 'primary.main'
                      : 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.light',
                      boxShadow: 2,
                    },
                  }}
                >
                  <CardActionArea
                    onClick={() => handleMetricToggle(key as MetricType)}
                    sx={{ height: '100%' }}
                  >
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                        <Box
                          sx={{
                            width: 48,
                            height: 48,
                            borderRadius: 2,
                            bgcolor: alpha(def.color, 0.125),
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: def.color,
                          }}
                        >
                          {def.icon}
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {def.label}
                            </Typography>
                            {config.metrics.includes(key as MetricType) && (
                              <Chip label={t('builder.selected')} size="small" color="primary" />
                            )}
                          </Box>
                          <Typography variant="caption" color="text.secondary">
                            {def.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>

          {config.metrics.length > 0 && (
            <Alert severity="info" sx={{ mt: 3 }}>
              {t('builder.selectedMetrics', { count: config.metrics.length })}: {config.metrics.map((m) => METRIC_DEFINITIONS[m].label).join(', ')}
            </Alert>
          )}
        </Box>
      )}

      {/* Step 2: Time Range and Filters */}
      {activeStep === 1 && (
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('builder.steps.timeAndFilters')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('builder.timeAndFiltersDesc')}
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('builder.timeRange')}
                </Typography>

                <Stack spacing={2}>
                  <TextField
                    label={t('builder.dateFrom')}
                    type="date"
                    value={format(config.dateFrom, 'yyyy-MM-dd')}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, dateFrom: new Date(e.target.value) }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  <TextField
                    label={t('builder.dateTo')}
                    type="date"
                    value={format(config.dateTo, 'yyyy-MM-dd')}
                    onChange={(e) =>
                      setConfig((prev) => ({ ...prev, dateTo: new Date(e.target.value) }))
                    }
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />

                  <FormControl fullWidth>
                    <InputLabel>{t('builder.grouping')}</InputLabel>
                    <Select
                      value={config.timeGrouping}
                      label={t('builder.grouping')}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          timeGrouping: e.target.value as TimeGrouping,
                        }))
                      }
                    >
                      <MenuItem value="day">{t('builder.groupings.day')}</MenuItem>
                      <MenuItem value="week">{t('builder.groupings.week')}</MenuItem>
                      <MenuItem value="month">{t('builder.groupings.month')}</MenuItem>
                      <MenuItem value="quarter">{t('builder.groupings.quarter')}</MenuItem>
                      <MenuItem value="year">{t('builder.groupings.year')}</MenuItem>
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t('builder.comparison')}</InputLabel>
                    <Select
                      value={config.comparison}
                      label={t('builder.comparison')}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          comparison: e.target.value as ComparisonType,
                        }))
                      }
                    >
                      <MenuItem value="none">{t('builder.comparisons.none')}</MenuItem>
                      <MenuItem value="previous-period">{t('builder.comparisons.previousPeriod')}</MenuItem>
                      <MenuItem value="year-over-year">{t('builder.comparisons.yearOverYear')}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <FilterIcon />
                  <Typography variant="subtitle1" fontWeight={600}>
                    {t('builder.filters')}
                  </Typography>
                </Box>

                <Stack spacing={2}>
                  <FormControl fullWidth>
                    <InputLabel>{t('builder.mechanics')}</InputLabel>
                    <Select
                      multiple
                      value={config.filters.mechanicIds || []}
                      label={t('builder.mechanics')}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          filters: { ...prev.filters, mechanicIds: e.target.value as string[] },
                        }))
                      }
                    >
                      {/*
                        Real mechanics, not the three invented names that
                        used to sit here with stub ids (#2712).
                      */}
                      <MenuItem value="all">{tc('all')}</MenuItem>
                      {(mechanics.data ?? []).map((mechanic) => (
                        <MenuItem key={mechanic.id} value={mechanic.id}>
                          {mechanic.firstName} {mechanic.lastName}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t('builder.serviceTypes')}</InputLabel>
                    <Select
                      multiple
                      value={config.filters.serviceTypes || []}
                      label={t('builder.serviceTypes')}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          filters: { ...prev.filters, serviceTypes: e.target.value as string[] },
                        }))
                      }
                    >
                      <MenuItem value="all">{tc('all')}</MenuItem>
                      {services.map((svc) => (
                        <MenuItem key={svc.id} value={svc.name}>
                          {svc.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <FormControl fullWidth>
                    <InputLabel>{t('builder.customerStatus')}</InputLabel>
                    <Select
                      multiple
                      value={config.filters.customerStatuses || []}
                      label={t('builder.customerStatus')}
                      onChange={(e) =>
                        setConfig((prev) => ({
                          ...prev,
                          filters: {
                            ...prev.filters,
                            customerStatuses: e.target.value as string[],
                          },
                        }))
                      }
                    >
                      <MenuItem value="all">{tc('all')}</MenuItem>
                      <MenuItem value="active">{t('builder.customerStatuses.active')}</MenuItem>
                      <MenuItem value="vip">{t('builder.customerStatuses.vip')}</MenuItem>
                      <MenuItem value="new">{t('builder.customerStatuses.new')}</MenuItem>
                    </Select>
                  </FormControl>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Step 3: Choose Visualization */}
      {activeStep === 2 && (
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('builder.steps.selectVisualization')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('builder.selectVisualizationDesc')}
          </Typography>

          <Grid container spacing={2}>
            {([
              { type: 'line', icon: <LineChartIcon />, label: t('builder.chartTypes.line'), desc: t('builder.chartTypes.lineDesc') },
              { type: 'area', icon: <TrendingUpIcon />, label: t('builder.chartTypes.area'), desc: t('builder.chartTypes.areaDesc') },
              { type: 'bar', icon: <BarChartIcon />, label: t('builder.chartTypes.bar'), desc: t('builder.chartTypes.barDesc') },
              { type: 'pie', icon: <PieChartIcon />, label: t('builder.chartTypes.pie'), desc: t('builder.chartTypes.pieDesc') },
              { type: 'table', icon: <TableChartIcon />, label: t('builder.chartTypes.table'), desc: t('builder.chartTypes.tableDesc') },
            ] as const).map(({ type, icon, label, desc }) => (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={type}>
                <Card
                  variant="outlined"
                  sx={{
                    border: 2,
                    borderColor: config.chartType === type ? 'primary.main' : 'divider',
                    transition: 'all 0.2s',
                    '&:hover': {
                      borderColor: 'primary.light',
                      boxShadow: 2,
                    },
                  }}
                >
                  <CardActionArea onClick={() => setConfig((prev) => ({ ...prev, chartType: type }))}>
                    <CardContent sx={{ textAlign: 'center', py: 3 }}>
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: 2,
                          bgcolor: config.chartType === type ? 'primary.light' : 'grey.100',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mx: 'auto',
                          mb: 2,
                          color: config.chartType === type ? 'primary.main' : 'text.secondary',
                        }}
                      >
                        {icon}
                      </Box>
                      <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                        {label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {desc}
                      </Typography>
                      {config.chartType === type && (
                        <Chip label={t('builder.selected')} size="small" color="primary" sx={{ mt: 1 }} />
                      )}
                    </CardContent>
                  </CardActionArea>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Step 4: Preview and Save */}
      {activeStep === 3 && (
        <Box>
          <Typography variant="h6" fontWeight={600} gutterBottom>
            {t('builder.steps.previewAndSave')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {t('builder.previewAndSaveDesc')}
          </Typography>

          <Grid container spacing={3}>
            <Grid size={{ xs: 12, lg: 8 }}>
              <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('builder.reportPreview')}
                </Typography>
                <Divider sx={{ my: 2 }} />
                {renderChart()}
              </Paper>
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider', mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                  {t('builder.reportDetails')}
                </Typography>
                <Divider sx={{ my: 2 }} />

                <Stack spacing={2}>
                  <TextField
                    label={t('builder.reportName')}
                    value={config.name}
                    onChange={(e) => setConfig((prev) => ({ ...prev, name: e.target.value }))}
                    placeholder={t('builder.reportNamePlaceholder')}
                    fullWidth
                    required
                  />

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('builder.metricsCount', { count: config.metrics.length })}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                      {config.metrics.map((metric) => (
                        <Chip
                          key={metric}
                          label={METRIC_DEFINITIONS[metric].label}
                          size="small"
                          sx={{ bgcolor: alpha(METRIC_DEFINITIONS[metric].color, 0.125) }}
                        />
                      ))}
                    </Box>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('builder.timeRange')}
                    </Typography>
                    <Typography variant="body2">
                      {format(config.dateFrom, 'd.M.yyyy', { locale: dateFnsLocale() })} -{' '}
                      {format(config.dateTo, 'd.M.yyyy', { locale: dateFnsLocale() })}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      {t('builder.visualization')}
                    </Typography>
                    <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                      {config.chartType === 'line' && t('builder.chartTypes.line')}
                      {config.chartType === 'area' && t('builder.chartTypes.area')}
                      {config.chartType === 'bar' && t('builder.chartTypes.bar')}
                      {config.chartType === 'pie' && t('builder.chartTypes.pie')}
                      {config.chartType === 'table' && t('builder.chartTypes.table')}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ my: 2 }} />

                <Stack spacing={1}>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSaveReport}
                    fullWidth
                  >
                    {t('builder.saveReport')}
                  </Button>

                  <Button
                    variant="outlined"
                    startIcon={<DownloadIcon />}
                    onClick={handleExport}
                    fullWidth
                  >
                    {t('builder.exportCsv')}
                  </Button>

                  <Button variant="outlined" startIcon={<ScheduleIcon />} fullWidth disabled>
                    {t('builder.scheduleAutoSend')}
                  </Button>
                </Stack>
              </Paper>

              <Alert severity="info" sx={{ fontSize: '0.875rem' }}>
                {t('builder.savedReportsHint')}
              </Alert>
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Navigation Buttons */}
      <Paper elevation={0} sx={{ p: 2, mt: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button disabled={activeStep === 0} onClick={handleBack}>
            {tc('back')}
          </Button>
          <Box sx={{ display: 'flex', gap: 2 }}>
            {activeStep < steps.length - 1 && (
              <Button variant="contained" onClick={handleNext}>
                {tc('next')}
              </Button>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
}
